// src/hooks/useContactData.ts
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from './useWorkspaces';

export const DEFAULT_IMPORTANT_DATE_LABELS = ['Birthday', 'Anniversary', "Kid's Birthday"];

export function useContactData() {
  const { currentWorkspace } = useWorkspaces();

  const { data: options = {
    relationship: [],
    type: [],
    rank: [],
    status: []
  }, isLoading: optionsLoading } = useQuery({
    queryKey: ['contactOptions', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return {};

      console.log('[useContactData] Fetching contact options from API');

      const { data, error } = await supabase
        .from('contact_options')
        .select('*')
        .eq('workspace_id', currentWorkspace.id);

      if (error) throw error;

      console.log('[useContactData] Contact options fetched successfully');

      return data.reduce((acc, option) => {
        if (!acc[option.category]) acc[option.category] = [];
        acc[option.category].push(option.value);
        return acc;
      }, {});
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes in React Query
  });

  const { data: groupNames = [] } = useQuery({
    queryKey: ['groupNames', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('view_group_counts')
        .select('group_name')
        .eq('workspace_id', currentWorkspace.id)
        .order('group_name');
      if (error) throw error;
      return [...new Set((data || []).map((r: any) => r.group_name as string))];
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  const { data: interestNames = [] } = useQuery({
    queryKey: ['interestNames', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase.rpc('get_workspace_interests', {
        p_workspace_id: currentWorkspace.id,
      });
      if (error) throw error;
      return (data || []).map((r: any) => r.interest as string);
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  const { data: importantDateLabels = DEFAULT_IMPORTANT_DATE_LABELS } = useQuery({
    queryKey: ['importantDateLabels', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return DEFAULT_IMPORTANT_DATE_LABELS;
      const { data, error } = await supabase.rpc('get_workspace_important_date_labels', {
        p_workspace_id: currentWorkspace.id,
      });
      if (error) throw error;
      const dbLabels = (data || []).map((r: any) => r.label as string);
      return Array.from(new Set([...DEFAULT_IMPORTANT_DATE_LABELS, ...dbLabels])).sort();
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    options,
    optionsLoading,
    groupNames,
    interestNames,
    importantDateLabels,
  };
}
